import { cn } from '@seen/ui'
import type { ComponentPropsWithoutRef, JSX } from 'react'

export type FacesheetCardHeaderProps = ComponentPropsWithoutRef<'div'>

// Title row of a facesheet section card. It pins to the top of the facesheet
// scroll viewport while its card is in view, the card content slides behind
// its translucent veil, and — because a sticky element cannot leave its
// parent — the next card pushes it out (Figma 205-2724 and the frames to its
// right).
//
// Sticky assumes the facesheet's header layout: there the per-tab ScrollArea
// viewport starts below the static header card and tab bar, so titles pin
// under the tab bar and content clips at the viewport edge.
// `--facesheet-sticky-top` is a seam for layouts that overlay content with a
// measured header (unset it means 0px); it carries the card's whole resting
// offset, gap included, so pinning parks this row's top edge exactly where the
// first card's already sits rather than against the header container's
// border/shadow (Figma 346-16938).
//
// The negative margins bleed the row over the card's padding *and* its 1px
// border so the row draws its own copy of the card's rounded top border above
// the translucent veil (Figma 208-3464) — otherwise the veil sits inside the
// card and the pinned row has no visible top edge. FacesheetCard pads p-3, so
// the bleed is calc(0.75rem + 1px). The row itself carries no border, so its
// padding includes the 1px the border used to occupy.
//
// Veil and blur live on an absolutely-positioned -z-10 child instead of the
// row: `backdrop-filter` on an element with a partially-rounded border box is
// exactly the shape Chromium's compositor gets wrong (the blurred backdrop
// reverts to square corners once the pinned row is composited during scroll),
// which lets white veil corners poke past the rounded arc. Nesting the blur
// layer inside a rounded overflow-hidden wrapper clips it to the radius
// geometrically, so no square corner can render no matter how the layer is
// composited.
//
// The border ring is its own z-[-5] overlay painted above the veil and the
// corner wedges. Anything drawn over the arc shaves it: the wedge feather
// used to cover its outer half-pixel and the veil's clip antialiasing washed
// its inner edge, so pinned corners read thinner than the straight borders
// (the at-rest look hid this because the card's identical border doubles up
// underneath). Insetting the veil's radius instead would leave an unfrosted
// sliver along the arc, so the opaque border simply paints last.
//
// While pinned mid-card, the card's own straight left/right borders slide
// behind the row and poke past its rounded corners (the region outside a
// border-radius arc is transparent, not clipped), and the card's shadow-sm
// hugs those borders ~2px outside the row. The -z-20 before/after wedges
// repaint that outside-the-arc corner region — extended 3px past the row's
// edges to catch the shadow spill — in the panel background (`--background`,
// which the card background also matches in both themes), so the radius
// visually clips both; everywhere else they paint background-on-background
// and are invisible. Below the arc's tangent point the spill is left alone:
// it matches the shadow the card draws beside its border at rest. The
// gradient's transparent stop ends 0.5px inside the radius so the wedge
// feathers into the border's antialiased outer edge, and the border overlay
// above repaints whatever the feather grazes. The wedges reach outside the
// card's padding box, so FacesheetCard must not clip horizontal overflow:
// Chrome clips descendants at the padding box even with a permissive
// overflow-clip-margin, which severed the wedges and let the borders show.
//
// While pinned, the card's own content keeps scrolling up through the gap
// above this row, so the -z-20 band covers it — `--facesheet-card-gap` tall,
// which is exactly the distance to the header container's veil. It has to
// ride the row rather than sit at a fixed spot in the panel: the card's
// clip-path makes it an atomic stacking context, so anything outside paints
// over the card whole, row included, and would slice this row as the card's
// bottom pushes it up and away. Riding along, band and row cover a
// continuous strip down from the band's top no matter where the row sits, so
// the row un-pins and scrolls off cleanly. Above the row's resting position
// the card's clip-path trims the band to 4px, which lands in the panel gap
// short of the previous card's shadow — by the time content reaches the
// band, the card's top has scrolled past and the band paints in full.
export function FacesheetCardHeader({
  className,
  children,
  ...delegated
}: FacesheetCardHeaderProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2',
        '-mx-[calc(0.75rem+1px)] -mt-[calc(0.75rem+1px)] sticky top-(--facesheet-sticky-top,0px) z-10 rounded-t-lg px-[calc(0.75rem+1px)] pt-[calc(0.75rem+1px)]',
        // pb-2 is the gap to the body. An empty or folded card has no next
        // sibling (Radix unmounts closed content), so the gap must not sit
        // under the title row on top of the card's p-3.
        'has-[+*]:-mb-2 has-[+*]:pb-2',
        'before:left-[-3px] before:-z-20 before:absolute before:top-0 before:h-(--radius) before:w-[calc(var(--radius)+3px)] before:bg-[radial-gradient(circle_at_100%_100%,transparent_calc(var(--radius)-0.5px),hsl(var(--background))_var(--radius))]',
        'after:right-[-3px] after:-z-20 after:absolute after:top-0 after:h-(--radius) after:w-[calc(var(--radius)+3px)] after:bg-[radial-gradient(circle_at_0%_100%,transparent_calc(var(--radius)-0.5px),hsl(var(--background))_var(--radius))]',
        className
      )}
      {...delegated}
    >
      <div
        aria-hidden
        className="absolute right-[-3px] bottom-full left-[-3px] -z-20 h-(--facesheet-card-gap,0px) bg-background"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 overflow-hidden rounded-t-lg"
      >
        <div className="absolute inset-0 rounded-t-lg bg-white/90 backdrop-blur-[2.5px] dark:bg-zinc-950/90" />
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[-5] rounded-t-lg border-x border-t border-zinc-200 dark:border-zinc-800"
      />
      {children}
    </div>
  )
}
